import { useCallback, useEffect, useRef, useState } from 'react';
import JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { UA } from 'jssip/lib/UA';
import {
  getMySipCredentials,
  setMyDnd,
  startCallLog,
  endCallLog,
  lookupCaller,
  type CallStatus,
  type CallerInfo,
  type SipCredentialRow,
} from '@/lib/sipApi';

// Without a STUN server, the browser's RTCPeerConnection only gathers
// "host" (local-network) ICE candidates — useless to a PBX out on the
// public internet the moment the staff member's browser is behind any
// NAT (virtually always). That's what silently broke audio: signaling
// (SIP) worked fine over the WSS connection either way, but RTP never
// found a route. A public STUN server gets the browser its real
// server-reflexive (public) address so media actually flows.
const STUN_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }];

export type RegistrationStatus = 'idle' | 'no_credentials' | 'connecting' | 'registered' | 'unregistered' | 'failed';
export type CallState = 'idle' | 'dialing' | 'ringing' | 'active' | 'ended';
export type MicPermission = 'unknown' | 'granted' | 'denied';

export interface IncomingCallInfo {
  number: string;
  displayName: string;
}

// Real Asterisk cause strings JsSIP surfaces on a 'failed' RTCSession event
// (JsSIP.C.causes) — mapped onto core_call_log's fixed status set.
function mapEndStatus(
  direction: 'inbound' | 'outbound' | null,
  wasEstablished: boolean,
  manualReject: boolean,
  cause: string | undefined,
): CallStatus {
  if (wasEstablished) return 'completed';
  if (manualReject) return 'rejected';
  if (cause === JsSIP.C.causes.BUSY) return 'busy';
  if (cause === JsSIP.C.causes.CANCELED) return direction === 'inbound' ? 'missed' : 'canceled';
  if (cause === JsSIP.C.causes.NO_ANSWER || cause === JsSIP.C.causes.EXPIRES) {
    return direction === 'inbound' ? 'missed' : 'no_answer';
  }
  if (cause === JsSIP.C.causes.REJECTED) return direction === 'inbound' ? 'missed' : 'no_answer';
  return 'failed';
}

// Ring pattern: two 400ms tones (~440Hz/480Hz beat, close to a standard
// dial-tone-adjacent ring) separated by a pause, looped — synthesized with
// Web Audio rather than shipping/hosting a ringtone file. Stops the moment
// the caller hangs up (JsSIP fires 'ended'/'failed' -> finishCall) or the
// call is answered/rejected, same as any real phone.
function startRingtone(ctxRef: { current: AudioContext | null }, stopRef: { current: (() => void) | null }) {
  if (typeof AudioContext === 'undefined' || stopRef.current) return;
  if (!ctxRef.current) ctxRef.current = new AudioContext();
  const ctx = ctxRef.current;
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout>;

  const ringOnce = () => {
    if (stopped) return;
    [440, 480].forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.value = 0.05;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    });
    timeoutId = setTimeout(ringOnce, 1200);
  };
  ringOnce();
  stopRef.current = () => {
    stopped = true;
    clearTimeout(timeoutId);
    stopRef.current = null;
  };
}

// Client-side JsSIP/WebRTC softphone registered directly against the
// staff member's own SIP extension (core_sip_credentials) — no bridge/
// gateway server, same PBX (ip.rxkseoul.com) desktop Microsip already uses
// for plain UDP extensions, just registering as a separate "SIP WebRTC
// Device" extension over its WebSocket endpoint instead. Mounted once at the app
// root (see SipPhoneContext.tsx / App.tsx), the same "persists across
// navigation" pattern as UserRoleProvider — so a call can ring no matter
// which admin page the staff member is on, not only while the Phone page
// itself happens to be mounted. `enabled` gates real registration off for
// anyone without a staff role (the public storefront shares this app).
export function useSipPhone(enabled: boolean) {
  const [credentials, setCredentials] = useState<SipCredentialRow | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus>('idle');
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [micPermission, setMicPermission] = useState<MicPermission>('unknown');

  const [callState, setCallState] = useState<CallState>('idle');
  const [callDirection, setCallDirection] = useState<'inbound' | 'outbound' | null>(null);
  const [remoteNumber, setRemoteNumber] = useState('');
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const [muted, setMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [lastEndedStatus, setLastEndedStatus] = useState<CallStatus | null>(null);
  const [lastCallLogId, setLastCallLogId] = useState<string | null>(null);
  const [callerInfo, setCallerInfo] = useState<CallerInfo | null>(null);

  // Second leg for "add to conference" — a real 3-way call, mixed locally in
  // the browser (see updateAudioMix below) rather than needing an Asterisk-
  // side conference bridge, so it works with nothing more than the two SIP
  // legs we already know how to place.
  const [confState, setConfState] = useState<'idle' | 'dialing' | 'ringing' | 'active'>('idle');
  const [confRemoteNumber, setConfRemoteNumber] = useState('');
  const [confLabel, setConfLabel] = useState('');

  // Do Not Disturb — declining every inbound call the instant it arrives
  // (before it ever rings on this browser) is functionally identical to
  // being pulled from the ring group's hunt list: Asterisk's `ringall`
  // strategy already moves straight on to the next member the moment one
  // destination comes back busy (see ext-group/600 in the PBX dialplan), so
  // this needs nothing on the Asterisk side at all.
  const [dnd, setDnd] = useState(false);
  const dndRef = useRef(false);
  useEffect(() => {
    dndRef.current = dnd;
  }, [dnd]);

  const uaRef = useRef<UA | null>(null);
  const sessionRef = useRef<RTCSession | null>(null);
  const confSessionRef = useRef<RTCSession | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const callStartRef = useRef<number | null>(null);
  const wasEstablishedRef = useRef(false);
  const manualRejectRef = useRef(false);
  const callLogIdRef = useRef<string | null>(null);
  const wasTransferredRef = useRef(false);
  const transferTargetRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ringCtxRef = useRef<AudioContext | null>(null);
  const stopRingRef = useRef<(() => void) | null>(null);
  const notificationRef = useRef<Notification | null>(null);

  // Remote audio streams keyed by session, and the Web Audio plumbing used to
  // mix them into one output the instant a second (conference) leg joins.
  // With just one stream we skip the mixer entirely and play it straight —
  // no need to pay for an AudioContext on every ordinary 2-party call.
  const remoteStreamsRef = useRef(new Map<RTCSession, MediaStream>());
  const mixCtxRef = useRef<AudioContext | null>(null);
  const mixDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const mixSourcesRef = useRef(new Map<RTCSession, MediaStreamAudioSourceNode>());

  if (!audioRef.current && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.autoplay = true;
    // Some browsers are stricter about autoplay on an <audio> element that
    // was never actually attached to the document — appending it (hidden)
    // makes it behave like any other on-page media element instead of a
    // detached one, which is one less thing that can silently block sound.
    if (typeof document !== 'undefined') {
      audioRef.current.style.display = 'none';
      document.body.appendChild(audioRef.current);
    }
  }

  const updateAudioMix = useCallback(() => {
    if (!audioRef.current) return;
    const streams = Array.from(remoteStreamsRef.current.values());
    if (streams.length <= 1) {
      // Tear down the mixer if it was ever spun up (conference just ended) —
      // otherwise its two dangling source nodes keep pulling audio into a
      // destination stream nothing is playing anymore.
      if (mixCtxRef.current) {
        mixSourcesRef.current.forEach((node) => node.disconnect());
        mixSourcesRef.current.clear();
        mixCtxRef.current.close().catch(() => {});
        mixCtxRef.current = null;
        mixDestRef.current = null;
      }
      audioRef.current.srcObject = streams[0] ?? null;
      audioRef.current.play().catch((err) => console.error('rxk phone: audio playback failed', err));
      return;
    }
    // Two or more remote streams — mix them into one so the agent hears
    // everyone at once through the single <audio> element.
    if (!mixCtxRef.current) mixCtxRef.current = new AudioContext();
    const ctx = mixCtxRef.current;
    if (!mixDestRef.current) mixDestRef.current = ctx.createMediaStreamDestination();
    remoteStreamsRef.current.forEach((stream, session) => {
      if (mixSourcesRef.current.has(session)) return;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(mixDestRef.current!);
      mixSourcesRef.current.set(session, source);
    });
    audioRef.current.srcObject = mixDestRef.current.stream;
    audioRef.current.play().catch((err) => console.error('rxk phone: audio playback failed', err));
  }, []);

  // Best-effort permission probe — doesn't block registration (that needs
  // no mic), just surfaces a clear "you need to allow the mic" state before
  // the staff member tries to place/answer a call. A denial here isn't
  // fatal: the browser will ask again (or JsSIP's own 'getusermediafailed'
  // fires) the moment a real call needs the mic.
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    // 'microphone' isn't in the standard PermissionName union in all lib.dom
    // versions (it's a widely-supported but still Chromium-flavored
    // extension) — `as any` here rather than naming a lib.dom type this
    // project's eslint config's no-undef doesn't recognize.
    navigator.permissions
      ?.query({ name: 'microphone' } as any)
      .then((status) => {
        setMicPermission(status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'unknown');
        status.onchange = () => setMicPermission(status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'unknown');
      })
      .catch(() => {});
  }, []);

  // Ask for notification permission up front (once, for any signed-in
  // staff member) rather than waiting for the first incoming call — by
  // then it's too late to prompt without missing that exact call, and
  // browsers won't let a notification-triggering prompt happen from
  // inside an event handler that isn't a direct user gesture anyway.
  useEffect(() => {
    if (!enabled || typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [enabled]);

  const requestMicPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicPermission('granted');
      return true;
    } catch {
      setMicPermission('denied');
      return false;
    }
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopAlert = useCallback(() => {
    stopRingRef.current?.();
    notificationRef.current?.close();
    notificationRef.current = null;
  }, []);

  const finishCall = useCallback((cause?: string) => {
    stopAlert();
    stopTimer();
    // The primary leg ending takes the whole conference down with it — don't
    // strand the added party on hold forever talking to no one.
    confSessionRef.current?.terminate();
    confSessionRef.current = null;
    setConfState('idle');
    setConfRemoteNumber('');
    setConfLabel('');
    const transferred = wasTransferredRef.current;
    const status: CallStatus = transferred ? 'transferred' : mapEndStatus(callDirection, wasEstablishedRef.current, manualRejectRef.current, cause);
    const durationSeconds = callStartRef.current ? Math.round((Date.now() - callStartRef.current) / 1000) : null;
    if (callLogIdRef.current) {
      // Persist the raw SIP cause alongside the mapped status — only
      // meaningfully different from `status` for the 'failed' bucket
      // (busy/no_answer/etc. are already self-explanatory), but stored
      // whenever present so a genuine failure can actually be diagnosed
      // from the DB instead of needing to reproduce it live.
      endCallLog(callLogIdRef.current, status, durationSeconds, transferred ? transferTargetRef.current : null, cause ?? null).catch(() => {});
      setLastCallLogId(callLogIdRef.current);
    }
    setLastEndedStatus(status);
    setCallState('ended');
    setIncomingCall(null);
    setCallSeconds(0);
    sessionRef.current = null;
    callStartRef.current = null;
    callLogIdRef.current = null;
    wasEstablishedRef.current = false;
    manualRejectRef.current = false;
    wasTransferredRef.current = false;
    transferTargetRef.current = null;
    remoteStreamsRef.current.clear();
    mixSourcesRef.current.forEach((node) => node.disconnect());
    mixSourcesRef.current.clear();
    mixCtxRef.current?.close().catch(() => {});
    mixCtxRef.current = null;
    mixDestRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
    // Briefly hold on 'ended' so the UI can show the outcome, then reset.
    setTimeout(() => {
      setCallState((s) => (s === 'ended' ? 'idle' : s));
      setCallDirection(null);
      setRemoteNumber('');
      setCallerInfo(null);
    }, 2500);
  }, [callDirection, stopAlert, stopTimer]);

  const bindSession = useCallback(
    (session: RTCSession, direction: 'inbound' | 'outbound') => {
      sessionRef.current = session;
      wasEstablishedRef.current = false;
      manualRejectRef.current = false;
      setCallDirection(direction);
      setMuted(false);
      const remoteUser = session.remote_identity?.uri?.user ?? '';
      setRemoteNumber(remoteUser);
      setCallState(direction === 'inbound' ? 'ringing' : 'dialing');
      setCallerInfo(null);
      setLastCallLogId(null);
      lookupCaller(remoteUser).then(setCallerInfo).catch(() => {});

      if (direction === 'inbound') {
        const displayName = session.remote_identity?.display_name || remoteUser;
        setIncomingCall({ number: remoteUser, displayName });
        startCallLog('inbound', remoteUser)
          .then((row) => (callLogIdRef.current = row.id))
          .catch(() => {});

        startRingtone(ringCtxRef, stopRingRef);
        // Desktop notification so a call isn't missed just because the
        // staff member is on a different admin page/tab — Answer/Reject
        // still only happen from the Phone page itself; clicking the
        // notification just brings the tab into focus.
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const n = new Notification(`Incoming call — ${displayName}`, {
            body: 'Click to open the Phone page and answer.',
            tag: 'rxk-incoming-call',
            requireInteraction: true,
          });
          n.onclick = () => {
            window.focus();
            n.close();
          };
          notificationRef.current = n;
        }
      }

      session.on('progress', () => {
        if (direction === 'outbound') setCallState('ringing');
      });

      session.on('confirmed', () => {
        wasEstablishedRef.current = true;
        setCallState('active');
        setIncomingCall(null);
        callStartRef.current = Date.now();
        setCallSeconds(0);
        stopTimer();
        timerRef.current = setInterval(() => {
          if (callStartRef.current) setCallSeconds(Math.round((Date.now() - callStartRef.current) / 1000));
        }, 1000);
      });

      session.on('ended', (e) => {
        remoteStreamsRef.current.delete(session);
        updateAudioMix();
        finishCall(e?.cause);
      });
      session.on('failed', (e) => {
        remoteStreamsRef.current.delete(session);
        updateAudioMix();
        finishCall(e?.cause);
      });

      session.on('peerconnection', ({ peerconnection }) => {
        peerconnection.addEventListener('track', (event: RTCTrackEvent) => {
          // event.streams[0] can be empty depending on how the far end's SDP
          // is structured (no msid grouping) — build a stream from the track
          // itself in that case rather than silently assigning `undefined`.
          remoteStreamsRef.current.set(session, event.streams[0] ?? new MediaStream([event.track]));
          updateAudioMix();
        });
      });

      session.on('getusermediafailed', () => {
        setMicPermission('denied');
        finishCall('Microphone unavailable');
      });
    },
    [finishCall, stopTimer, updateAudioMix],
  );

  // Registration lifecycle — one UA for as long as `enabled` stays true
  // (a signed-in staff member's whole session, per SipPhoneContext), torn
  // down if it ever flips to false (e.g. sign-out) so a stale socket
  // never lingers registered under a session that's gone.
  useEffect(() => {
    if (!enabled) {
      setRegistrationStatus('idle');
      return;
    }
    let cancelled = false;

    getMySipCredentials()
      .then((cred) => {
        if (cancelled) return;
        if (!cred || !cred.is_active) {
          setRegistrationStatus('no_credentials');
          return;
        }
        setCredentials(cred);
        setDnd(cred.dnd_enabled);
        dndRef.current = cred.dnd_enabled;

        const socket = new JsSIP.WebSocketInterface(cred.ws_url);
        const ua = new JsSIP.UA({
          sockets: [socket],
          uri: `sip:${cred.extension}@${cred.sip_server}`,
          password: cred.sip_password,
          display_name: cred.display_name || cred.extension,
          register: true,
          session_timers: false,
        });
        uaRef.current = ua;

        ua.on('connecting', () => setRegistrationStatus('connecting'));
        ua.on('registered', () => {
          setRegistrationStatus('registered');
          setRegistrationError(null);
        });
        ua.on('unregistered', () => setRegistrationStatus('unregistered'));
        ua.on('registrationFailed', (e) => {
          setRegistrationStatus('failed');
          setRegistrationError(e?.cause ?? 'Registration failed');
        });
        ua.on('newRTCSession', ({ originator, session }) => {
          // Outgoing sessions are bound directly off ua.call()'s return
          // value in call() below — only bind here for a call someone else
          // is placing TO this extension.
          if (originator === 'remote') {
            if (dndRef.current) {
              // Decline before this ever rings here — the ring group
              // (ringall) moves straight on to the next member, same as it
              // would if this extension were simply offline.
              session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
              return;
            }
            if (sessionRef.current) {
              // Already on a call — politely decline instead of silently
              // dropping (JsSIP.C.causes.BUSY -> a proper 486 Busy Here).
              session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
              return;
            }
            bindSession(session, 'inbound');
          }
        });

        ua.start();
      })
      .catch((e) => {
        if (!cancelled) {
          setRegistrationStatus('failed');
          setRegistrationError(e instanceof Error ? e.message : 'Failed to load SIP credentials');
        }
      });

    return () => {
      cancelled = true;
      stopTimer();
      uaRef.current?.stop();
      uaRef.current = null;
      sessionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const call = useCallback(
    async (number: string, orderId?: string) => {
      const target = number.trim();
      if (!target || !uaRef.current || registrationStatus !== 'registered' || sessionRef.current) return;
      if (micPermission !== 'granted') {
        const ok = await requestMicPermission();
        if (!ok) return;
      }
      const session = uaRef.current.call(`sip:${target}@${credentials?.sip_server}`, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: STUN_SERVERS },
      });
      bindSession(session, 'outbound');
      // Kick the <audio> element's autoplay permission off the same click
      // that started the call — some browsers are stricter about a play()
      // call made later, asynchronously, from inside a 'track' event
      // handler that isn't itself a direct user gesture.
      audioRef.current?.play().catch(() => {});
      startCallLog('outbound', target, orderId)
        .then((row) => (callLogIdRef.current = row.id))
        .catch(() => {});
    },
    [bindSession, credentials, micPermission, registrationStatus, requestMicPermission],
  );

  const answer = useCallback(async () => {
    if (!sessionRef.current) return;
    stopAlert();
    if (micPermission !== 'granted') {
      const ok = await requestMicPermission();
      if (!ok) return;
    }
    audioRef.current?.play().catch(() => {});
    sessionRef.current.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: STUN_SERVERS },
    });
  }, [micPermission, requestMicPermission, stopAlert]);

  const reject = useCallback(() => {
    manualRejectRef.current = true;
    stopAlert();
    sessionRef.current?.terminate();
  }, [stopAlert]);

  const hangup = useCallback(() => {
    sessionRef.current?.terminate();
  }, []);

  const toggleDnd = useCallback(() => {
    setDnd((prev) => {
      const next = !prev;
      dndRef.current = next;
      setMyDnd(next).catch(() => {
        // Best-effort persistence — if it fails to save, the local toggle
        // (and therefore actual call behavior) still takes effect for the
        // rest of this session; it just won't survive a reload.
      });
      return next;
    });
  }, []);

  const toggleMute = useCallback(() => {
    if (!sessionRef.current) return;
    if (muted) {
      sessionRef.current.unmute({ audio: true });
      confSessionRef.current?.unmute({ audio: true });
      setMuted(false);
    } else {
      sessionRef.current.mute({ audio: true });
      confSessionRef.current?.mute({ audio: true });
      setMuted(true);
    }
  }, [muted]);

  const bindConferenceSession = useCallback(
    (session: RTCSession) => {
      confSessionRef.current = session;
      setConfRemoteNumber(session.remote_identity?.uri?.user ?? '');
      setConfState('dialing');

      session.on('progress', () => setConfState('ringing'));

      session.on('confirmed', () => {
        setConfState('active');
        // The whole reason we dialed this second leg was to merge it in —
        // unhold the original party the instant the added party answers.
        sessionRef.current?.unhold();
      });

      const cleanup = () => {
        remoteStreamsRef.current.delete(session);
        updateAudioMix();
        if (confSessionRef.current !== session) return;
        confSessionRef.current = null;
        setConfState('idle');
        setConfRemoteNumber('');
        setConfLabel('');
        // The added party leaving (declined, busy, hung up) shouldn't leave
        // the original caller on hold indefinitely.
        sessionRef.current?.unhold();
      };
      session.on('ended', cleanup);
      session.on('failed', cleanup);

      session.on('peerconnection', ({ peerconnection }) => {
        peerconnection.addEventListener('track', (event: RTCTrackEvent) => {
          remoteStreamsRef.current.set(session, event.streams[0] ?? new MediaStream([event.track]));
          updateAudioMix();
        });
      });
    },
    [updateAudioMix],
  );

  // Real 3-way calling: dial a second party while the first stays on hold,
  // then auto-merge (see bindConferenceSession's 'confirmed' handler) —
  // mixed locally via Web Audio, so it needs nothing from Asterisk beyond
  // two ordinary calls.
  const startConference = useCallback(
    (targetExtension: string, targetLabel?: string) => {
      if (!uaRef.current || !sessionRef.current || callState !== 'active' || confSessionRef.current || !credentials) return;
      setConfLabel(targetLabel || targetExtension);
      sessionRef.current.hold();
      const session = uaRef.current.call(`sip:${targetExtension}@${credentials.sip_server}`, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: STUN_SERVERS },
      });
      bindConferenceSession(session);
    },
    [bindConferenceSession, callState, credentials],
  );

  // Drops just the added party, keeping the original call going — the
  // 'ended' handler in bindConferenceSession takes care of unholding it.
  const endConferenceLeg = useCallback(() => {
    confSessionRef.current?.terminate();
  }, []);

  // Blind transfer via SIP REFER — Asterisk takes it from here: once the
  // REFER itself is accepted (202), we drop our own leg and the caller is
  // bridged straight to targetExtension. We don't wait for the transfer
  // target to actually answer (that would need tracking the REFER
  // subscription's NOTIFY events too) — same trade-off a real desk phone's
  // "blind transfer" button makes.
  const transfer = useCallback(
    (targetExtension: string, targetLabel?: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (!sessionRef.current || callState !== 'active' || !credentials) {
          reject(new Error('No active call to transfer'));
          return;
        }
        const target = `sip:${targetExtension}@${credentials.sip_server}`;
        transferTargetRef.current = targetLabel || targetExtension;
        try {
          sessionRef.current.refer(target, {
            eventHandlers: {
              requestSucceeded: () => {
                wasTransferredRef.current = true;
                sessionRef.current?.terminate();
                resolve();
              },
              requestFailed: () => {
                transferTargetRef.current = null;
                reject(new Error('Transfer failed — that extension may be offline.'));
              },
            },
          });
        } catch (e) {
          transferTargetRef.current = null;
          reject(e instanceof Error ? e : new Error('Transfer failed'));
        }
      });
    },
    [callState, credentials],
  );

  const sendDTMF = useCallback((tone: string) => {
    if (!sessionRef.current || callState !== 'active') return;
    try {
      sessionRef.current.sendDTMF(tone, { duration: 160, interToneGap: 100 });
    } catch {
      // Ignore — sending DTMF outside an active session is a no-op, not an error worth surfacing.
    }
  }, [callState]);

  return {
    hasCredentials: !!credentials,
    extension: credentials?.extension ?? null,
    registrationStatus,
    registrationError,
    micPermission,
    requestMicPermission,
    callState,
    callDirection,
    remoteNumber,
    incomingCall,
    muted,
    callSeconds,
    lastEndedStatus,
    lastCallLogId,
    callerInfo,
    call,
    answer,
    reject,
    hangup,
    toggleMute,
    sendDTMF,
    transfer,
    confState,
    confRemoteNumber,
    confLabel,
    startConference,
    endConferenceLeg,
    dnd,
    toggleDnd,
  };
}
